import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db, auth } from '../firebase';
import NavBar from '../components/NavBar';
import Footer from '../components/Footer';
import ImageOverlay from '../components/ImageOverlay';
import { placeholderFor } from '../utils/listingPlaceholder';

/**
 * ArchivedListing - Displays a read-only view of a pedal as it was at trade/purchase time
 * Uses snapshot data stored in trade/purchase documents, not live listing data
 */
const ArchivedListing = () => {
  const { type, id, pedalType, pedalId } = useParams(); // type = 'trade' or 'purchase', id = tradeId/purchaseId, pedalType = 'my' or 'their' (legacy), pedalId = actual pedal ID
  const navigate = useNavigate();
  
  const [pedal, setPedal] = useState(null);
  const [ownerInfo, setOwnerInfo] = useState({ username: '', badge: null });
  const [currentUser, setCurrentUser] = useState(null);
  const [overlayImage, setOverlayImage] = useState({ isOpen: false, url: '', alt: '', index: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notLoggedIn, setNotLoggedIn] = useState(false);
  const [imageArray, setImageArray] = useState([]);
  const [tradeStatus, setTradeStatus] = useState(null); // Store trade/purchase status for back button

  // Build image array when pedal data loads
  useEffect(() => {
    if (pedal?.images) {
      const images = [];
      const imageKeys = ['top', 'back', 'upper', 'lower', 'detail1', 'detail2', 'detail3', 'detail4'];
      const imageLabels = {
        top: 'Top View',
        back: 'Back View',
        upper: 'Upper View',
        lower: 'Lower View',
        detail1: 'Detail 1',
        detail2: 'Detail 2',
        detail3: 'Detail 3',
        detail4: 'Detail 4'
      };
      
      imageKeys.forEach(key => {
        if (pedal.images[key]) {
          images.push({
            url: pedal.images[key],
            alt: imageLabels[key],
            key: key
          });
        }
      });
      
      setImageArray(images);
    }
  }, [pedal]);

  const openImageOverlay = (imageUrl, imageAlt) => {
    const index = imageArray.findIndex(img => img.url === imageUrl);
    setOverlayImage({
      isOpen: true,
      url: imageUrl,
      alt: imageAlt,
      index: index >= 0 ? index : 0
    });
  };

  const closeImageOverlay = () => {
    setOverlayImage({ isOpen: false, url: '', alt: '', index: 0 });
  };

  const navigateToNextImage = () => {
    if (overlayImage.index < imageArray.length - 1) {
      const nextIndex = overlayImage.index + 1;
      const nextImage = imageArray[nextIndex];
      setOverlayImage({
        isOpen: true,
        url: nextImage.url,
        alt: nextImage.alt,
        index: nextIndex
      });
    }
  };

  const navigateToPrevImage = () => {
    if (overlayImage.index > 0) {
      const prevIndex = overlayImage.index - 1;
      const prevImage = imageArray[prevIndex];
      setOverlayImage({
        isOpen: true,
        url: prevImage.url,
        alt: prevImage.alt,
        index: prevIndex
      });
    }
  };

  const getTradeBadge = (count) => {
    if (count === undefined || count === null || isNaN(count)) return 0;
    return count;
  };

  const getMembershipBadge = (membershipTier) => {
    switch (membershipTier) {
      case 'premium_basic':
        return (
          <div className="flex items-center bg-blue-100 text-blue-800 px-2 py-1 rounded-full font-medium" style={{ fontSize: '12px' }}>
            <span className="bg-blue-600 text-white px-1.5 py-0.5 rounded font-bold mr-1" style={{ fontSize: '11px' }}>9V</span>
            Member
          </div>
        );
      case 'premium_plus':
        return (
          <div className="flex items-center bg-purple-100 text-purple-800 px-2 py-1 rounded-full font-medium" style={{ fontSize: '12px' }}>
            <span className="bg-purple-600 text-white px-1.5 py-0.5 rounded font-bold mr-1" style={{ fontSize: '11px' }}>18V</span>
            Member
          </div>
        );
      default:
        return (
          <div className="flex items-center bg-gray-100 text-gray-600 px-2 py-1 rounded-full font-medium" style={{ fontSize: '12px' }}>
            Free Member
          </div>
        );
    }
  };

  const getMembershipProfileBadge = (membershipTier) => {
    switch (membershipTier) {
      case 'premium_basic':
        return (
          <div
            style={{
              position: 'absolute',
              top: '-4px',
              left: '-4px',
              backgroundColor: '#2563eb',
              color: 'white',
              fontWeight: 'bold',
              borderRadius: '4px',
              width: '20px',
              height: '12px',
              fontSize: '0.6rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '1px solid #fff',
              boxShadow: '0 0 2px rgba(0,0,0,0.3)',
            }}
          >
            9V
          </div>
        );
      case 'premium_plus':
        return (
          <div
            style={{
              position: 'absolute',
              top: '-4px',
              left: '-4px',
              backgroundColor: '#7c3aed',
              color: 'white',
              fontWeight: 'bold',
              borderRadius: '4px',
              width: '20px',
              height: '12px',
              fontSize: '0.6rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '1px solid #fff',
              boxShadow: '0 0 2px rgba(0,0,0,0.3)',
            }}
          >
            18V
          </div>
        );
      default:
        return null;
    }
  };

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      setCurrentUser(user);
      if (!user) { setNotLoggedIn(true); setLoading(false); }
    });

    const loadArchivedListing = async () => {
      try {
        console.log(`📸 Loading archived listing: type=${type}, id=${id}, pedalType=${pedalType}`);
        
        // Determine collection based on type
        const collectionName = type === 'trade' ? 'tradeOffers' : 'purchaseOffers';
        const docRef = doc(db, collectionName, id);
        const docSnap = await getDoc(docRef);
        
        if (!docSnap.exists()) {
          // Auth may not have resolved yet on a fresh load / pull-to-refresh
          // reload. If the user is genuinely logged out, the onAuthStateChanged
          // handler above shows the login screen (notLoggedIn). If they're
          // logged in, this effect re-runs with currentUser set and we surface
          // the real not-found below. Until auth resolves, stay in the loading
          // state instead of flashing a login/not-found message.
          if (!currentUser) {
            sessionStorage.setItem('returnAfterLogin', window.location.pathname);
            return;
          }
          setError('Trade or purchase not found');
          setLoading(false);
          return;
        }
        
        const data = docSnap.data();
        console.log(`📦 Document data:`, data);
        
        // Store the trade/purchase status for the back button
        setTradeStatus(data.status);
        
        // Extract pedal snapshot based on type
        let pedalData = null;
        
        if (type === 'trade') {
          // For trades, use pedalSnapshots
          if (!data.pedalSnapshots) {
            setError('No snapshot data available for this trade');
            setLoading(false);
            return;
          }
          
          // Wait for currentUser to be available
          if (!currentUser) {
            console.log('⏳ Waiting for user authentication...');
            return; // Don't set error, just wait for auth
          }
          
          const initiatorPedals = (data.pedalSnapshots.initiatorPedals && Array.isArray(data.pedalSnapshots.initiatorPedals))
            ? data.pedalSnapshots.initiatorPedals
            : (data.pedalSnapshots.initiatorPedal ? [data.pedalSnapshots.initiatorPedal] : []);
          const responderPedal = data.pedalSnapshots.responderPedal;
          
          console.log('🔍 DEBUG ArchivedListing:');
          console.log('   Current User UID:', currentUser.uid);
          console.log('   Initiator Pedals count:', initiatorPedals.length);
          console.log('   Responder Pedal ownerId:', responderPedal?.ownerId);
          console.log('   PedalType:', pedalType);
          console.log('   PedalId:', pedalId);
          console.log('   Trade fromUserId:', data.fromUserId);
          console.log('   Trade toUserId:', data.toUserId);
          
          if (pedalId) {
            // Direct pedal ID lookup for multi-pedal trades
            pedalData = initiatorPedals.find(p => p?.id === pedalId)
              || (responderPedal?.id === pedalId ? responderPedal : null);
            if (!pedalData) {
              console.error('❌ Could not find pedal with ID:', pedalId);
              setError('Pedal not found in trade snapshot');
              setLoading(false);
              return;
            }
            console.log('✅ Matched pedal by ID:', pedalId);
          } else if (pedalType === 'my') {
            // Legacy path: match by ownership
            const myInitiatorPedal = initiatorPedals.find(p => p?.ownerId === currentUser.uid);
            if (myInitiatorPedal) {
              console.log('✅ Matched initiator pedal as "my" pedal');
              pedalData = myInitiatorPedal;
            } else if (responderPedal?.ownerId === currentUser.uid) {
              console.log('✅ Matched responder pedal as "my" pedal');
              pedalData = responderPedal;
            } else {
              console.error('❌ Could not match any pedal to current user');
              setError('Could not determine your pedal');
              setLoading(false);
              return;
            }
          } else if (pedalType === 'their') {
            const theirInitiatorPedal = initiatorPedals.find(p => p?.ownerId !== currentUser.uid);
            if (theirInitiatorPedal) {
              console.log('✅ Matched initiator pedal as "their" pedal');
              pedalData = theirInitiatorPedal;
            } else if (responderPedal?.ownerId !== currentUser.uid) {
              console.log('✅ Matched responder pedal as "their" pedal');
              pedalData = responderPedal;
            } else {
              console.error('❌ Could not match any pedal to other user');
              setError('Could not determine their pedal');
              setLoading(false);
              return;
            }
          } else {
            setError('Invalid pedal type specified');
            setLoading(false);
            return;
          }
        } else if (type === 'purchase') {
          // For purchases, use pedalSnapshot.targetPedal
          if (!data.pedalSnapshot?.targetPedal) {
            setError('No snapshot data available for this purchase');
            setLoading(false);
            return;
          }
          
          pedalData = data.pedalSnapshot.targetPedal;
        } else {
          setError('Invalid type specified');
          setLoading(false);
          return;
        }
        
        if (!pedalData) {
          setError('Pedal data not found in snapshot');
          setLoading(false);
          return;
        }
        
        console.log(`✅ Pedal snapshot loaded:`, pedalData);
        setPedal(pedalData);
        
        // Fetch owner's username and profile
        if (pedalData.ownerId) {
          // PII isolation: read sanitized public mirror.
          const ownerRef = doc(db, 'publicProfiles', pedalData.ownerId);
          const ownerSnap = await getDoc(ownerRef);
          
          if (ownerSnap.exists()) {
            const ownerData = ownerSnap.data();
            setOwnerInfo({
              username: ownerData.username || 'Unknown User',
              badge: getTradeBadge(ownerData.completedTrades || 0),
              photoURL: ownerData.photoURL || '/images/default-avatar.png',
              membershipTier: ownerData.membershipTier || 'free',
              averageRating: ownerData.averageRating || 0
            });
            console.log(`👤 Owner data loaded: ${ownerData.username}`);
          }
        }
        
        setLoading(false);
      } catch (err) {
        console.error('❌ Error loading archived listing:', err);
        setError('Failed to load archived listing');
        setLoading(false);
      }
    };
    
    loadArchivedListing();
    
    return () => unsubscribe();
  }, [type, id, pedalType, pedalId, currentUser]);

  if (notLoggedIn) {
    return (
      <div>
        <NavBar />
        <div style={{ minHeight: '50vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2rem', paddingTop: '125px' }}>
          <div style={{ maxWidth: '500px', width: '100%', backgroundColor: '#e8f4fd', border: '2px solid #5A9ECB', borderRadius: '12px', padding: '2rem', textAlign: 'center', fontFamily: 'Nunito, sans-serif' }}>
            <h2 style={{ fontSize: '1.5rem', fontWeight: '700', color: '#1a5276', marginBottom: '1rem' }}>Log In to View This Listing</h2>
            <p style={{ fontSize: '1rem', color: '#2c3e50', lineHeight: '1.6', marginBottom: '1.5rem' }}>You need to be logged in to view archived listing details.</p>
            <Link to="/login" style={{ display: 'inline-block', backgroundColor: '#5A9ECB', color: 'white', fontWeight: '600', padding: '0.75rem 2rem', border: 'none', borderRadius: '6px', fontSize: '1rem', textDecoration: 'none', fontFamily: 'Nunito, sans-serif' }}>Go to Login</Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (loading) {
    return (
      <div>
        <NavBar />
        <div className="min-h-screen flex items-center justify-center" style={{ paddingTop: '125px' }}>
          <div className="text-center">
            <div className="text-xl font-semibold mb-2">Loading archived listing...</div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <NavBar />
        <div className="min-h-screen flex items-center justify-center" style={{ paddingTop: '125px' }}>
          <div className="text-center">
            <div className="text-xl font-semibold mb-2 text-red-600">{error}</div>
            <button
              onClick={() => navigate(-1)}
              className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              Go Back
            </button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const renderSmallImage = (src, alt) => (
    <div 
      onClick={() => openImageOverlay(src, alt)}
      style={{ cursor: 'pointer', width: '140px', flexShrink: 0 }}
    >
      <img
        src={src}
        alt={alt}
        style={{
          width: '140px',
          height: '140px',
          objectFit: 'cover',
          borderRadius: '6px',
          transition: 'transform 0.2s, box-shadow 0.2s'
        }}
        onMouseEnter={(e) => {
          e.target.style.transform = 'scale(1.05)';
          e.target.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.2)';
        }}
        onMouseLeave={(e) => {
          e.target.style.transform = 'scale(1)';
          e.target.style.boxShadow = 'none';
        }}
      />
    </div>
  );

  const renderMobileSmallImage = (src, alt) => (
    <div onClick={() => openImageOverlay(src, alt)} style={{ cursor: 'pointer', width: '70px', flexShrink: 0 }}>
      <img src={src} alt={alt} style={{ borderRadius: '6px', objectFit: 'cover' }} />
    </div>
  );

  return (
    <div className='archived-listing-page' style={{ backgroundColor: '#F7F3EB', minHeight: 'calc(100vh - var(--navbar-height, 125px))', display: 'flex', flexDirection: 'column', padding: '0 1rem', overflowX: 'hidden' }}>
      <style jsx>{`
        @media (max-width: 768px) {
          .archived-listing-page {
            padding: 0 0.5rem !important;
          }
          .archived-info-box {
            padding: 1rem !important;
            width: 100% !important;
            max-width: 100% !important;
          }
          .archived-top-section {
            flex-direction: column !important;
            gap: 0 !important;
          }
          .archived-large-image {
            width: 100% !important;
            height: auto !important;
            max-width: 100% !important;
          }
          .archived-small-images-row {
            display: flex !important;
            flex-wrap: wrap !important;
            justify-content: flex-start !important;
            margin-top: 1rem !important;
            margin-bottom: 2.5rem !important;
            width: 100% !important;
          }
          .archived-small-images-row > div {
            width: 70px !important;
            flex-shrink: 0 !important;
            margin-right: calc((100% - (70px * 4)) / 3) !important;
          }
          .archived-small-images-row > div:nth-child(4n) {
            margin-right: 0 !important;
          }
          .archived-small-images-row img {
            width: 70px !important;
            height: 70px !important;
          }
          .archived-mobile-profile-container {
            display: grid !important;
            grid-template-columns: 1fr auto !important;
            gap: 2rem !important;
            align-items: flex-start !important;
            margin-bottom: 0.5rem !important;
          }
          .archived-mobile-left-column {
            grid-column: 1 !important;
            display: flex !important;
            flex-direction: column !important;
            gap: 1rem !important;
          }
          .archived-mobile-make-model {
            margin: 0 !important;
          }
          .archived-mobile-make-model h2 {
            margin-bottom: 0.35rem !important;
          }
          .archived-mobile-profile-section {
            flex-shrink: 0 !important;
            grid-column: 2 !important;
          }
          .archived-mobile-username-btn {
            display: block !important;
            text-align: center !important;
            margin-left: auto !important;
            margin-right: auto !important;
            max-width: 140px !important;
            line-height: 1.2 !important;
          }
          .archived-mobile-details {
            display: block !important;
            grid-column: 1 !important;
            grid-row: 2 !important;
            margin-top: 0 !important;
          }
          .archived-desktop-right-side {
            display: none !important;
          }
          .archived-desktop-small-images {
            display: none !important;
          }
        }
        @media (min-width: 769px) {
          .archived-small-images-row {
            display: none !important;
          }
          .archived-mobile-profile-container {
            display: none !important;
          }
          .archived-mobile-details {
            display: none !important;
          }
        }
      `}</style>
      <ImageOverlay 
        isOpen={overlayImage.isOpen}
        imageUrl={overlayImage.url}
        imageAlt={overlayImage.alt}
        onClose={closeImageOverlay}
        onNext={navigateToNextImage}
        onPrev={navigateToPrevImage}
        hasNext={overlayImage.index < imageArray.length - 1}
        hasPrev={overlayImage.index > 0}
      />
      
      {/* Archived Listing Banner */}
      <div className='mb-4 text-center'>
        <div style={{
          display: 'inline-block',
          backgroundColor: '#fef3c7',
          border: '2px solid #f59e0b',
          color: '#92400e',
          fontWeight: 'bold',
          fontSize: '0.875rem',
          padding: '6px 16px',
          borderRadius: '20px',
          marginTop: '8px',
          marginBottom: '8px',
          boxShadow: '0 2px 4px rgba(245, 158, 11, 0.3)'
        }}>
          Archived Listing from {type === 'trade' ? 'Trade' : 'Purchase'}
        </div>
      </div>

      {/* Pedal Information Box */}
      <div className='archived-info-box' style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
        padding: '2rem',
        paddingBottom: '2.5rem',
        marginBottom: '1.5rem',
        marginTop: '0.5rem',
        maxWidth: '900px',
        width: '100%',
        margin: '0.5rem auto 1.5rem auto'
      }}>
        
        {/* Top Section: Large Image Left, Profile + Details Right */}
        <div className="archived-top-section" style={{ display: 'flex', gap: '2rem', alignItems: 'flex-start', marginBottom: '2rem' }}>
          
          {/* Large Top Image */}
          {(pedal.images?.top || placeholderFor(pedal).url) && (
            <div 
              onClick={() => openImageOverlay(pedal.images?.top || placeholderFor(pedal).url, 'Top View')}
              style={{ cursor: 'pointer', flexShrink: 0 }}
            >
              <img
                className="archived-large-image"
                src={pedal.images?.top || placeholderFor(pedal).url}
                alt='Top View'
                style={{
                  width: '480px',
                  height: '480px',
                  objectFit: 'cover',
                  borderRadius: '10px',
                  transition: 'transform 0.2s, box-shadow 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'scale(1.02)';
                  e.target.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.2)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'scale(1)';
                  e.target.style.boxShadow = 'none';
                }}
              />
            </div>
          )}
          
          {/* Small Images Row - Mobile Only */}
          <div className="archived-small-images-row">
            {pedal.images?.back && renderMobileSmallImage(pedal.images.back, 'Back View')}
            {pedal.images?.upper && renderMobileSmallImage(pedal.images.upper, 'Upper View')}
            {pedal.images?.lower && renderMobileSmallImage(pedal.images.lower, 'Lower View')}
            {pedal.images?.detail1 && renderMobileSmallImage(pedal.images.detail1, 'Detail 1')}
            {pedal.images?.detail2 && renderMobileSmallImage(pedal.images.detail2, 'Detail 2')}
            {pedal.images?.detail3 && renderMobileSmallImage(pedal.images.detail3, 'Detail 3')}
            {pedal.images?.detail4 && renderMobileSmallImage(pedal.images.detail4, 'Detail 4')}
          </div>
          
          {/* Mobile: Make/Model Left, Profile Right */}
          <div className="archived-mobile-profile-container">
            <div className="archived-mobile-left-column">
              <div className="archived-mobile-make-model" style={{ lineHeight: '1.0', textAlign: 'left' }}>
                <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', margin: '0 0 1rem 0', padding: '0', lineHeight: '1.0', color: '#374151' }}>
                  {pedal.brand || pedal.make}
                </h2>
                <h3 style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#6b7280', margin: '0', padding: '0', lineHeight: '1.0' }}>
                  {pedal.model}
                </h3>
              </div>
              
              <div className="archived-mobile-details" style={{ fontSize: '1.05rem', color: '#374151', textAlign: 'left' }}>
                {pedal.price && <p style={{ margin: '0 0 6px 0', padding: '0', lineHeight: '1.2' }}><strong>Price:</strong> ${pedal.price}</p>}
                {pedal.condition && <p style={{ margin: '0 0 6px 0', padding: '0', lineHeight: '1.2' }}><strong>Condition:</strong> {pedal.condition}</p>}
                {pedal.effectTypes && pedal.effectTypes.length > 0 && (
                  <p style={{ margin: '0 0 6px 0', padding: '0', lineHeight: '1.2' }}>
                    <strong>Effect Type{pedal.effectTypes.length > 1 ? 's' : ''}:</strong> {pedal.effectTypes.join(', ')}
                  </p>
                )}
                {pedal.decade && <p style={{ margin: '0 0 6px 0', padding: '0', lineHeight: '1.2' }}><strong>Decade:</strong> {pedal.decade}</p>}
                {pedal.color && <p style={{ margin: '0 0 6px 0', padding: '0', lineHeight: '1.2' }}><strong>Color:</strong> {pedal.color}</p>}
                {pedal.location && <p style={{ margin: '0 0 6px 0', padding: '0', lineHeight: '1.2' }}><strong>Location:</strong> {pedal.location}</p>}
              </div>
            </div>
            
            <div className="archived-mobile-profile-section">
              <button
                className="archived-mobile-username-btn"
                onClick={() => navigate(`/profile/${pedal.ownerId}`)}
                style={{ fontSize: '1.15rem', color: '#374151', fontWeight: '700', fontFamily: 'Nunito, sans-serif', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'none', padding: '0', marginBottom: '0.25rem', wordWrap: 'break-word', whiteSpace: 'normal', textAlign: 'center', maxWidth: '100%', overflowWrap: 'break-word' }}
                onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
              >
                {ownerInfo.username}
              </button>
              <div style={{ marginBottom: '0.35rem', textAlign: 'center' }}>
                {getMembershipBadge(ownerInfo.membershipTier)}
              </div>
              <div style={{ position: 'relative', width: '50px', height: '50px', marginBottom: '0.35rem', marginLeft: 'auto', marginRight: 'auto' }}>
                <button
                  onClick={() => navigate(`/profile/${pedal.ownerId}`)}
                  style={{ width: '50px', height: '50px', borderRadius: '50%', border: '2px solid #D1D5DB', cursor: 'pointer', padding: '0', background: 'none', overflow: 'hidden', transition: 'opacity 0.2s' }}
                  onMouseEnter={(e) => e.target.style.opacity = '0.8'}
                  onMouseLeave={(e) => e.target.style.opacity = '1'}
                >
                  <img src={ownerInfo.photoURL || '/images/default-avatar.png'} alt='User Avatar' style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} />
                </button>
                {getMembershipProfileBadge(ownerInfo.membershipTier)}
                {ownerInfo.badge !== null && ownerInfo.badge !== undefined && (
                  <div style={{ position: 'absolute', bottom: '-3px', right: '-3px', backgroundColor: 'gold', color: '#000', fontWeight: 'bold', borderRadius: '50%', width: '18px', height: '18px', fontSize: '0.65rem', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '2px solid #fff', boxShadow: '0 0 5px rgba(0,0,0,0.3)' }}>
                    {ownerInfo.badge}
                  </div>
                )}
              </div>
              {ownerInfo.averageRating > 0 ? (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px', fontSize: '0.875rem' }}>
                  <span style={{ color: '#f59e0b' }}>★</span>
                  <span style={{ fontWeight: '600', color: '#374151' }}>{ownerInfo.averageRating.toFixed(1)}</span>
                </div>
              ) : (
                <div style={{ fontSize: '0.8rem', color: '#6b7280', textAlign: 'center' }}>No ratings yet</div>
              )}
            </div>
          </div>
          
          {/* Desktop Right Side: Profile + Details */}
          <div className="archived-desktop-right-side" style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
            
            {/* Owner Profile */}
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', marginBottom: '0.5rem' }}>
              <button
                onClick={() => navigate(`/profile/${pedal.ownerId}`)}
                style={{ fontSize: '1.15rem', color: '#374151', textAlign: 'left', fontWeight: '700', fontFamily: 'Nunito, sans-serif', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'none', padding: '0', marginBottom: '0.25rem' }}
                onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
              >
                {ownerInfo.username}
              </button>
              <div style={{ marginBottom: '0.35rem' }}>
                {getMembershipBadge(ownerInfo.membershipTier)}
              </div>
              <div style={{ position: 'relative', width: '50px', height: '50px', marginBottom: '0.35rem' }}>
                <button
                  onClick={() => navigate(`/profile/${pedal.ownerId}`)}
                  style={{ width: '50px', height: '50px', borderRadius: '50%', border: '2px solid #D1D5DB', cursor: 'pointer', padding: '0', background: 'none', overflow: 'hidden', transition: 'opacity 0.2s' }}
                  onMouseEnter={(e) => e.target.style.opacity = '0.8'}
                  onMouseLeave={(e) => e.target.style.opacity = '1'}
                >
                  <img src={ownerInfo.photoURL || '/images/default-avatar.png'} alt='User Avatar' style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} />
                </button>
                {getMembershipProfileBadge(ownerInfo.membershipTier)}
                {ownerInfo.badge !== null && ownerInfo.badge !== undefined && (
                  <div style={{ position: 'absolute', bottom: '-3px', right: '-3px', backgroundColor: 'gold', color: '#000', fontWeight: 'bold', borderRadius: '50%', width: '18px', height: '18px', fontSize: '0.65rem', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '2px solid #fff', boxShadow: '0 0 5px rgba(0,0,0,0.3)' }}>
                    {ownerInfo.badge}
                  </div>
                )}
              </div>
              {ownerInfo.averageRating > 0 ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '0.875rem' }}>
                  <span style={{ color: '#f59e0b' }}>★</span>
                  <span style={{ fontWeight: '600', color: '#374151' }}>{ownerInfo.averageRating.toFixed(1)}</span>
                </div>
              ) : (
                <div style={{ fontSize: '0.8rem', color: '#6b7280' }}>No ratings yet</div>
              )}
            </div>
            
            {/* Pedal Brand and Model */}
            <div style={{ lineHeight: '1.0', textAlign: 'left', marginTop: '1.25rem', marginBottom: '1.75rem' }}>
              <h2 style={{ fontSize: '1.875rem', fontWeight: 'bold', margin: '0 0 0.15rem 0', padding: '0', lineHeight: '1.0', color: '#374151' }}>
                {pedal.brand || pedal.make}
              </h2>
              <h3 style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#6b7280', margin: '0', padding: '0', lineHeight: '1.0' }}>
                {pedal.model}
              </h3>
            </div>
            
            {/* Pedal Details */}
            <div style={{ fontSize: '1.05rem', color: '#374151', textAlign: 'left' }}>
              {pedal.price && <p style={{ margin: '0 0 6px 0', padding: '0', lineHeight: '1.2' }}><strong>Price:</strong> ${pedal.price}</p>}
              {pedal.condition && <p style={{ margin: '0 0 6px 0', padding: '0', lineHeight: '1.2' }}><strong>Condition:</strong> {pedal.condition}</p>}
              {pedal.effectTypes && pedal.effectTypes.length > 0 && (
                <p style={{ margin: '0 0 6px 0', padding: '0', lineHeight: '1.2' }}>
                  <strong>Effect Type{pedal.effectTypes.length > 1 ? 's' : ''}:</strong> {pedal.effectTypes.join(', ')}
                </p>
              )}
              {pedal.decade && <p style={{ margin: '0 0 6px 0', padding: '0', lineHeight: '1.2' }}><strong>Decade:</strong> {pedal.decade}</p>}
              {pedal.color && <p style={{ margin: '0 0 6px 0', padding: '0', lineHeight: '1.2' }}><strong>Color:</strong> {pedal.color}</p>}
              {pedal.location && <p style={{ margin: '0 0 6px 0', padding: '0', lineHeight: '1.2' }}><strong>Location:</strong> {pedal.location}</p>}
            </div>
          </div>
        </div>

        {/* Row of Smaller Images - Desktop Only */}
        <div className="archived-desktop-small-images" style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'flex-start', gap: '16px', marginBottom: '3rem' }}>
          {pedal.images?.back && renderSmallImage(pedal.images.back, 'Back View')}
          {pedal.images?.upper && renderSmallImage(pedal.images.upper, 'Upper View')}
          {pedal.images?.lower && renderSmallImage(pedal.images.lower, 'Lower View')}
          {pedal.images?.detail1 && renderSmallImage(pedal.images.detail1, 'Detail 1')}
          {pedal.images?.detail2 && renderSmallImage(pedal.images.detail2, 'Detail 2')}
          {pedal.images?.detail3 && renderSmallImage(pedal.images.detail3, 'Detail 3')}
          {pedal.images?.detail4 && renderSmallImage(pedal.images.detail4, 'Detail 4')}
        </div>

        {/* Description */}
        {pedal.description && (
          <div style={{ marginTop: '1rem' }}>
            <h4 className='font-semibold mb-3 text-center'>Description</h4>
            <div style={{
              border: '1px solid #d1d5db',
              borderRadius: '8px',
              padding: '16px',
              backgroundColor: '#f9fafb'
            }}>
              <p className='text-gray-700' style={{ 
                margin: 0,
                wordWrap: 'break-word',
                overflowWrap: 'break-word',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word'
              }}>
                {pedal.description}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Back Link */}
      <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
        <button
          onClick={() => navigate(-1)}
          style={{ background: 'none', border: 'none', color: '#5A9ECB', cursor: 'pointer', fontSize: '16px', fontWeight: '500', textDecoration: 'none', padding: '0' }}
          onMouseEnter={(e) => e.target.style.opacity = '0.7'}
          onMouseLeave={(e) => e.target.style.opacity = '1'}
        >
          ← Back to {
            type === 'trade' 
              ? (tradeStatus === 'complete' || tradeStatus === 'completed' ? 'Completed Trades' : 'Accepted Trades')
              : (tradeStatus === 'complete' || tradeStatus === 'completed' ? 'Completed Purchases' : 'Accepted Purchases')
          }
        </button>
      </div>

      <Footer />
    </div>
  );
};

export default ArchivedListing;
